import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useRouteMatch } from 'react-router-dom';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import { computed } from 'mobx';
import * as Sentry from '@sentry/browser';

import useComponentMounted from '../../../hooks/useComponentMounted';
import User from '../../../Model/User';
import useToast from '../../hooks/useToast';
import { usePodFilter } from '../../../context/PodContext';
import ActiveClientsContext, { initialValues } from './ActiveClientsContext';
import texts from './texts.json';

const ActiveClientsContextProvider = ({
  children,
}) => {
  const { params: { userId: coachId } } = useRouteMatch();
  const { getFilteredDocs } = usePodFilter();
  const { showToast } = useToast();

  const [clientCollection, setClientCollection] = useState(null);
  const [isReady, setIsReady] = useState(initialValues.isReady);
  const [hasError, setHasError] = useState(initialValues.hasError);

  const isComponentMountedRef = useComponentMounted();

  useEffect(() => {
    setClientCollection(null);
    setIsReady(false);
    setHasError(false);
    if (!coachId) {
      return undefined;
    }

    let cancelled = false;
    const loadActiveClients = async () => {
      try {
        const collection = await User.getActiveUsersByCoach(coachId);
        if (cancelled || !isComponentMountedRef.current) {
          return;
        }
        setClientCollection(collection);
        // Only a successful load flips `isReady` — see the contract on the context.
        setIsReady(true);
      } catch (err) {
        Sentry.captureException(err, {
          extra: { message: 'Error loading active clients', coach: coachId },
        });
        if (cancelled || !isComponentMountedRef.current) {
          return;
        }
        setHasError(true);
        showToast(texts.loadError, { error: true });
      }
    };

    loadActiveClients();

    return () => {
      cancelled = true;
    };
  }, [
    coachId,
    isComponentMountedRef,
    showToast,
  ]);

  const activeClientDocs = useMemo(() => computed(
    () => getFilteredDocs(clientCollection ? clientCollection.docs : initialValues.activeClientDocs, 'id'),
  ), [
    clientCollection,
    getFilteredDocs,
  ]).get();

  const activeClientDocMap = useMemo(
    () => new Map(activeClientDocs.map((clientDoc) => [clientDoc.id, clientDoc])),
    [activeClientDocs],
  );

  const contextValue = useMemo(() => ({
    activeClientDocs,
    activeClientDocMap,
    isReady,
    hasError,
  }), [
    activeClientDocs,
    activeClientDocMap,
    isReady,
    hasError,
  ]);

  return (
    <ActiveClientsContext.Provider value={contextValue}>
      {children}
    </ActiveClientsContext.Provider>
  );
};

ActiveClientsContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default compose(
  observer,
)(ActiveClientsContextProvider);
